import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import LocationPicker from "@/components/LocationPicker";
import CustomDatePicker from "@/components/DateTimePicker";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, toEnglishDigits } from "@/utils/toEnglishDigits";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  TrashSimple,
  X,
  Path,
  ArrowLeft,
  Spinner,
  Car,
  Coins,
} from "@phosphor-icons/react";

const CAR_LABEL_AR = {
  gmc: "جمس",
  ford: "فورد",
  lexus: "لكزس",
  staria: "استاريا",
  hiace: "هايس",
  coaster: "كوستر",
  bus: "باص",
};

const emptyForm = {
  pickup_location: "",
  destination: "",
  car_type: "gmc",
  round_trip_price: "",
  one_way_price: "",
  is_active: true,
  valid_from: "",
  valid_to: "",
};

export default function DriverWageRules({
  endpointBase = "/admin/driver-wage-rules",
  pageTitle = "مسارات أجرة السائق",
  entityLabel = "السائق",
  // Extension slots — allow subclasses (e.g. ExternalDriverWageRules) to
  // inject extra required fields, validation, and a table column.
  extraFormDefaults = {},
  extraFormFields = null,       // (form, setForm, rules) => JSX
  extraSavePayload = () => ({}),  // (form) => extra body fields
  extraValidate = () => null,    // (form) => string | null (error msg)
  extraColumn = null,           // { header: string, cell: (rule) => JSX }
} = {}) {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [locations, setLocations] = useState({ pickups: [], destinations: [], all: [] });
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l, c] = await Promise.all([
        api.get(endpointBase),
        api.get("/admin/locations").catch(() => ({ data: { pickups: [], destinations: [], all: [] } })),
        api.get("/cars/capacity").catch(() => api.get("/cars").catch(() => ({ data: [] }))),
      ]);
      setRules(r.data || []);
      setLocations(l.data || { pickups: [], destinations: [], all: [] });
      setCars(
        (c.data || [])
          .map((x) => ({ id: x.id || x.car_type, name: x.name_ar || CAR_LABEL_AR[x.id || x.car_type] || (x.id || x.car_type) }))
          .filter((x) => x.id),
      );
    } catch (e) {
      toast.error("تعذر تحميل قواعد الأجرة");
    } finally {
      setLoading(false);
    }
  }, [endpointBase]);

  useEffect(() => { loadAll(); }, [loadAll]);

  function openCreate() {
    setEditId(null);
    setForm({ ...emptyForm, ...extraFormDefaults, car_type: cars[0]?.id || "gmc" });
    setModalOpen(true);
  }

  function openEdit(r) {
    setEditId(r.id);
    setForm({
      pickup_location: r.pickup_location || "",
      destination: r.destination || "",
      car_type: r.car_type || "gmc",
      round_trip_price: String(r.round_trip_price ?? ""),
      one_way_price: String(r.one_way_price ?? ""),
      is_active: !!r.is_active,
      valid_from: r.valid_from || "",
      valid_to: r.valid_to || "",
      // Hydrate any extra fields the extended page tracks (e.g.
      // external_driver_id on the external-drivers wage rules).
      ...Object.fromEntries(
        Object.keys(extraFormDefaults).map((k) => [k, r[k] ?? extraFormDefaults[k]])
      ),
    });
    setModalOpen(true);
  }

  async function save() {
    const pk = form.pickup_location.trim();
    const ds = form.destination.trim();
    if (!pk || !ds || !form.car_type) {
      toast.error("اختر الانطلاق والوجهة ونوع السيارة");
      return;
    }
    const extraErr = extraValidate(form);
    if (extraErr) {
      toast.error(extraErr);
      return;
    }
    const body = {
      pickup_location: pk,
      destination: ds,
      car_type: form.car_type,
      round_trip_price: Number(form.round_trip_price) || 0,
      one_way_price: Number(form.one_way_price) || 0,
      is_active: !!form.is_active,
      valid_from: form.valid_from || null,
      valid_to: form.valid_to || null,
      ...extraSavePayload(form),
    };
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`${endpointBase}/${editId}`, body);
        toast.success("تم تحديث القاعدة");
      } else {
        await api.post(endpointBase, body);
        toast.success("تم إنشاء القاعدة");
      }
      setModalOpen(false);
      loadAll();
    } catch (e) {
      toast.error("خطأ: " + formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function del(r) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`حذف القاعدة: ${r.pickup_location} → ${r.destination} (${CAR_LABEL_AR[r.car_type] || r.car_type})؟`)) return;
    try {
      await api.delete(`${endpointBase}/${r.id}`);
      toast.success("تم الحذف");
      loadAll();
    } catch (e) {
      toast.error("تعذر الحذف: " + formatApiError(e));
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) => {
      const carName = (CAR_LABEL_AR[r.car_type] || r.car_type || "").toLowerCase();
      return (
        (r.pickup_location || "").toLowerCase().includes(q) ||
        (r.destination || "").toLowerCase().includes(q) ||
        carName.includes(q)
      );
    });
  }, [rules, search]);

  return (
    <div className="driver-wage-rules-page min-h-screen bg-background text-white" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              data-testid="wage-rules-back-btn"
            >
              <ArrowLeft size={16} className="text-white/70" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "#C9A961" }}>
                <Coins size={22} weight="duotone" />
                {pageTitle}
              </h1>
              <p className="text-white/50 text-xs mt-1">تسعيرة {entityLabel} حسب المسار ونوع السيارة</p>
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            data-testid="wage-rules-add-btn"
            className="px-4 py-2 rounded-xl bg-primary text-black font-bold hover:bg-primary/90 inline-flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> قاعدة جديدة
          </button>
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 right-3 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث بمسار أو نوع سيارة…"
            className="w-full bg-[#0B1A3A] border border-primary/25 rounded-xl px-3 py-2.5 pe-9 text-white placeholder:text-white/40 focus:outline-none focus:border-primary/60"
            data-testid="wage-rules-search"
          />
        </div>

        {/* Table */}
        <section className="bg-[#0F2148]/70 border border-primary/20 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-white/50 flex items-center justify-center gap-2">
              <Spinner size={16} className="animate-spin" /> جاري التحميل…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-white/50">
              {search ? "لا نتائج لهذا البحث." : "لا توجد قواعد بعد. اضغط \"قاعدة جديدة\" لإضافة أول قاعدة."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-primary/[0.06] text-primary text-xs uppercase">
                  <tr>
                    {extraColumn && <th className="p-3 text-start whitespace-nowrap">{extraColumn.header}</th>}
                    <th className="p-3 text-start whitespace-nowrap">الانطلاق</th>
                    <th className="p-3 text-start whitespace-nowrap">الوجهة</th>
                    <th className="p-3 text-start whitespace-nowrap">السيارة</th>
                    <th className="p-3 text-start whitespace-nowrap">حركة كاملة</th>
                    <th className="p-3 text-start whitespace-nowrap">حركة فردية</th>
                    <th className="p-3 text-start whitespace-nowrap">الحالة</th>
                    <th className="p-3 text-start whitespace-nowrap">الصلاحية</th>
                    <th className="p-3 text-start whitespace-nowrap">إجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary/10">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-primary/[0.03]" data-testid={`wage-rule-row-${r.id}`}>
                      {extraColumn && <td className="p-3">{extraColumn.cell(r)}</td>}
                      <td className="p-3 font-semibold" style={{ color: "#C9A961" }}>{r.pickup_location}</td>
                      <td className="p-3 font-semibold" style={{ color: "#C9A961" }}>{r.destination}</td>
                      <td className="p-3 text-white/80">
                        <span className="inline-flex items-center gap-1">
                          <Car size={12} weight="duotone" />
                          {CAR_LABEL_AR[r.car_type] || r.car_type}
                        </span>
                      </td>
                      <td className="p-3 num font-bold" style={{ color: "#7DD3FC" }} dir="ltr">
                        <span dir="ltr">{fmtNum(r.round_trip_price)}</span> ر.س
                      </td>
                      <td className="p-3 num font-bold" style={{ color: "#C9A961" }} dir="ltr">
                        <span dir="ltr">{fmtNum(r.one_way_price)}</span> ر.س
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-[10px] font-bold ${
                            r.is_active
                              ? "bg-green-500/20 text-green-300 border border-green-500/40"
                              : "bg-red-500/20 text-red-300 border border-red-500/40"
                          }`}
                        >
                          {r.is_active ? "مفعّل" : "معطل"}
                        </span>
                      </td>
                      <td className="p-3 text-white/50 text-xs num" dir="ltr">
                        {r.valid_from || r.valid_to ? `${toEnglishDigits(r.valid_from) || "—"} → ${toEnglishDigits(r.valid_to) || "—"}` : "دائماً"}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            data-testid={`wage-rule-edit-${r.id}`}
                            className="p-1.5 rounded-lg bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25"
                            title="تعديل"
                          >
                            <PencilSimple size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            onClick={() => del(r)}
                            data-testid={`wage-rule-delete-${r.id}`}
                            className="p-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
                            title="حذف"
                          >
                            <TrashSimple size={13} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !saving && setModalOpen(false)}
          data-testid="wage-rule-modal"
        >
          <div
            className="bg-[#0F2148] border border-primary/40 rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-primary/20 flex items-center justify-between">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <Path size={16} weight="duotone" className="text-primary" />
                {editId ? "تعديل قاعدة أجرة" : "قاعدة أجرة جديدة"}
              </h3>
              <button
                type="button"
                onClick={() => !saving && setModalOpen(false)}
                className="text-white/60 hover:text-white"
                data-testid="wage-rule-modal-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {/* Extension: extra fields injected by wrappers (e.g.
                  external-driver picker) — rendered at top of modal. */}
              {typeof extraFormFields === "function" && extraFormFields(form, setForm, rules)}

              {/* Pickup — city chips + detail input (same UX as booking form) */}
              <LocationPicker
                label="نقطة الانطلاق"
                value={form.pickup_location}
                onChange={(v) => setForm({ ...form, pickup_location: v })}
                includeShrines={false}
                otherEndpointValue={form.destination}
                testIdPrefix="wage-rule-pickup"
                placeholder="الموقع التفصيلي (اختياري)"
              />

              {/* Destination — city chips + shrines/tour + detail input */}
              <LocationPicker
                label="الوجهة"
                value={form.destination}
                onChange={(v) => setForm({ ...form, destination: v })}
                includeShrines={true}
                pickupContext={form.pickup_location}
                otherEndpointValue={form.pickup_location}
                testIdPrefix="wage-rule-destination"
                placeholder="الموقع التفصيلي (اختياري)"
              />

              {/* Car type chips */}
              <div>
                <label className="block text-white/70 text-xs mb-1">نوع السيارة</label>
                <div className="flex flex-wrap gap-1.5">
                  {cars.length === 0 ? (
                    <span className="text-white/40 text-xs">لا توجد سيارات في الإعدادات</span>
                  ) : (
                    cars.map((c) => {
                      const active = form.car_type === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setForm({ ...form, car_type: c.id })}
                          data-testid={`wage-rule-car-${c.id}`}
                          className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                            active
                              ? "bg-primary text-black border-primary"
                              : "bg-[#0B1A3A] text-white/80 border-primary/25 hover:border-primary/60"
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-xs mb-1">حركة كاملة (ر.س)</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={form.round_trip_price}
                    onChange={(e) => setForm({ ...form, round_trip_price: toEnglishDigits(e.target.value) })}
                    className="w-full bg-[#0B1A3A] border border-primary/25 rounded-lg px-3 py-2 text-white num text-end focus:outline-none focus:border-primary/60"
                    data-testid="wage-rule-round-trip"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs mb-1">حركة فردية (ر.س)</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={form.one_way_price}
                    onChange={(e) => setForm({ ...form, one_way_price: toEnglishDigits(e.target.value) })}
                    className="w-full bg-[#0B1A3A] border border-primary/25 rounded-lg px-3 py-2 text-white num text-end focus:outline-none focus:border-primary/60"
                    data-testid="wage-rule-one-way"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Validity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-xs mb-1">صلاحية من (اختياري)</label>
                  <CustomDatePicker
                    value={form.valid_from}
                    onChange={(iso) => setForm({ ...form, valid_from: iso })}
                    placeholder="YYYY-MM-DD"
                    testId="wage-rule-valid-from"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs mb-1">صلاحية إلى (اختياري)</label>
                  <CustomDatePicker
                    value={form.valid_to}
                    onChange={(iso) => setForm({ ...form, valid_to: iso })}
                    placeholder="YYYY-MM-DD"
                    testId="wage-rule-valid-to"
                  />
                </div>
              </div>

              {/* Active toggle */}
              <label className="flex items-center justify-between p-3 rounded-lg bg-primary/[0.05] border border-primary/20 cursor-pointer">
                <span className="text-white/85 text-sm">القاعدة مفعّلة</span>
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="wage-rule-active"
                />
              </label>
            </div>

            <div className="p-4 border-t border-primary/20 flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="wage-rule-save"
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-black font-bold hover:bg-primary/90 disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                {saving ? <><Spinner size={14} className="animate-spin" /> جاري الحفظ…</> : "حفظ"}
              </button>
              <button
                type="button"
                onClick={() => !saving && setModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/80 hover:bg-white/10"
                data-testid="wage-rule-cancel"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
